import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { KYC } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Loader2, ShieldCheck, ImagePlus, Clock, XCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const KYCPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(undefined);
  const [form, setForm] = useState({
    full_name: '', document_type: 'aadhaar', document_number: '',
    document_url: '', selfie_url: '', notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState('');

  useEffect(() => {
    api.get('/kyc/me').then(({ data }) => {
      setExisting(data.kyc);
      if (data.kyc) {
        setForm({
          full_name: data.kyc.full_name || user?.name || '',
          document_type: data.kyc.document_type || 'aadhaar',
          document_number: data.kyc.document_number || '',
          document_url: data.kyc.document_url || '',
          selfie_url: data.kyc.selfie_url || '',
          notes: data.kyc.notes || '',
        });
      } else {
        setForm((f) => ({ ...f, full_name: user?.name || '' }));
      }
    }).catch(() => setExisting(null));
    // eslint-disable-next-line
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const upload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(target);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, [target]: url }));
      toast.success('Uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(''); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.document_url) { toast.error('Please upload your document image'); return; }
    setLoading(true);
    try {
      await api.post('/kyc/submit', form);
      toast.success('KYC submitted!', { description: 'Our team will review within 48 hours.' });
      const { data } = await api.get('/kyc/me');
      setExisting(data.kyc);
    } catch (err) {
      toast.error('Could not submit', { description: err?.response?.data?.detail });
    } finally { setLoading(false); }
  };

  const StatusBanner = () => {
    if (!existing) return null;
    const map = {
      pending: { Icon: Clock, color: 'text-amber-300', bg: 'bg-amber-500/10 border-amber-500/30', label: 'Under review', text: "We're reviewing your submission. You'll hear back within 48 hours." },
      approved: { Icon: CheckCircle2, color: 'text-emerald-300', bg: 'bg-emerald-500/10 border-emerald-500/30', label: 'Approved', text: 'Your verified badge has been upgraded.' },
      rejected: { Icon: XCircle, color: 'text-rose-300', bg: 'bg-rose-500/10 border-rose-500/30', label: 'Rejected', text: existing.rejection_reason || 'Your submission was rejected. Please re-submit with the requested changes.' },
    };
    const s = map[existing.status] || map.pending;
    return (
      <div className={`rounded-2xl border ${s.bg} p-5 mb-8 flex items-start gap-3`}>
        <s.Icon className={`h-5 w-5 ${s.color} mt-0.5`} />
        <div>
          <div className={`text-sm font-semibold ${s.color}`}>KYC {s.label}</div>
          <div className="text-sm text-slate-300 mt-1">{s.text}</div>
        </div>
      </div>
    );
  };

  if (existing === undefined) {
    return <div className="pt-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div>;
  }
  const locked = existing && (existing.status === 'pending' || existing.status === 'approved');

  return (
    <div className="pt-28 pb-24 max-w-2xl mx-auto px-6 sm:px-12">
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-4 text-xs uppercase tracking-widest text-emerald-300">
          <ShieldCheck className="h-3.5 w-3.5" /> Trust & verification
        </div>
        <h1 className="font-display text-4xl font-bold text-white tracking-tight mb-3">Verify your <span className="ai-text">identity</span></h1>
        <p className="text-slate-400 max-w-xl mx-auto">Verified artists get a 3× higher booking rate and unlock Professional status.</p>
      </div>

      <StatusBanner />

      <form onSubmit={submit} className="glass-strong rounded-3xl border border-white/10 p-8 space-y-5">
        <div>
          <Label className="text-xs text-slate-400 uppercase tracking-wider">Legal full name *</Label>
          <Input required value={form.full_name} onChange={update('full_name')} disabled={locked} data-testid={KYC.fullNameInput} className="bg-white/5 border-white/10 mt-1" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Document type *</Label>
            <Select value={form.document_type} onValueChange={(v) => setForm((f) => ({ ...f, document_type: v }))} disabled={locked}>
              <SelectTrigger data-testid={KYC.docTypeSelect} className="bg-white/5 border-white/10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 text-slate-200">
                <SelectItem value="aadhaar">Aadhaar</SelectItem>
                <SelectItem value="pan">PAN Card</SelectItem>
                <SelectItem value="passport">Passport</SelectItem>
                <SelectItem value="driving_license">Driving License</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Document number *</Label>
            <Input required value={form.document_number} onChange={update('document_number')} disabled={locked} data-testid={KYC.docNumberInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Document image *</Label>
            {form.document_url && <img src={form.document_url} alt="" className="mt-2 h-32 w-full object-cover rounded-lg border border-white/10" />}
            <label className={`mt-2 inline-flex items-center gap-2 text-xs ${locked ? 'text-slate-500' : 'text-cyan-300 hover:text-cyan-200 cursor-pointer'}`}>
              <ImagePlus className="h-3.5 w-3.5" /> {uploading === 'document_url' ? 'Uploading…' : 'Upload front side'}
              <input type="file" accept="image/*" hidden disabled={locked || uploading} data-testid={KYC.docUploadInput} onChange={(e) => upload(e, 'document_url')} />
            </label>
          </div>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Selfie (optional)</Label>
            {form.selfie_url && <img src={form.selfie_url} alt="" className="mt-2 h-32 w-full object-cover rounded-lg border border-white/10" />}
            <label className={`mt-2 inline-flex items-center gap-2 text-xs ${locked ? 'text-slate-500' : 'text-cyan-300 hover:text-cyan-200 cursor-pointer'}`}>
              <ImagePlus className="h-3.5 w-3.5" /> {uploading === 'selfie_url' ? 'Uploading…' : 'Upload selfie'}
              <input type="file" accept="image/*" hidden disabled={locked || uploading} data-testid={KYC.selfieUploadInput} onChange={(e) => upload(e, 'selfie_url')} />
            </label>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-400 uppercase tracking-wider">Notes (optional)</Label>
          <Textarea rows={3} value={form.notes} onChange={update('notes')} disabled={locked} className="bg-white/5 border-white/10 mt-1" />
        </div>
        {!locked && (
          <Button type="submit" disabled={loading} data-testid={KYC.submitButton} className="w-full btn-rainbow text-white border-0 rounded-xl py-6 text-base">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</> : <><ShieldCheck className="h-4 w-4 mr-2" />Submit for verification</>}
          </Button>
        )}
      </form>
    </div>
  );
};

export default KYCPage;
